import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useRouter } from 'next/router';
import { set, z } from 'zod';
import type { SafeParseError } from 'zod';
import { FormattedMessage } from 'react-intl';
import * as Sentry from '@sentry/nextjs';
import cls from 'classnames';

import Fields, { FieldType } from '@/components/common/Fields';
import type { FieldValue } from '@/components/common/Fields';
import Icon from '@/components/common/icons';
import { contactUs } from '@/services/contact';
import type { ContactUsParams } from '@/services/contact';
import { isType, isUndefined } from '@/utils/is';
import { simpleDebounce } from '@/utils';
import { EMAIL_REGEXP, PHONE_REGEXP } from '@/constants/regexp';
import { fullCountry } from '@/constants/country';
import styles from './Form.module.scss';
import CountryInput from './CountryInput';
import { addGtagEvent } from '@/utils/addGtagEvent';
import { gaEventName } from '@/constants/googleAnalytics';

interface FormField {
  name: string;
  required: boolean;
  type: FieldType;
  step: 1 | 2;
  style?: CSSProperties;
  props?: Record<string, unknown>;
}

const messages = {
  placeholder: {
    input: {
      'en-US': 'Please enter',
      'zh-CN': '请输入',
    },
    select: {
      'en-US': 'Please select',
      'zh-CN': '请选择',
    },
  },
};

const industryLabels = {
  'e-commerce': {
    'en-US': 'E-commerce',
    'zh-CN': '电商',
  },
  games: {
    'en-US': 'Games',
    'zh-CN': '游戏',
  },
  app: {
    'en-US': 'APP',
    'zh-CN': 'APP',
  },
  'dtc-brand': {
    'en-US': 'DTC Brand',
    'zh-CN': 'DTC品牌',
  },
};

const consultingServicesLabels = {
  'influencer-campaigns': {
    'en-US': 'Influencer Campaigns',
    'zh-CN': '全球网红营销',
  },
  'integrated-marketing': {
    'en-US': 'Integrated Marketing',
    'zh-CN': '品牌全案营销',
  },
  'paid-social-advertising': {
    'en-US': 'Paid Social Advertising',
    'zh-CN': '海外广告投放',
  },
  'live-streaming-services': {
    'en-US': 'Live Streaming Services',
    'zh-CN': '海外直播带货',
  },
};

const formLabels = {
  name: {
    'en-US': 'Full name',
    'zh-CN': '名字',
  },
  email: {
    'en-US': 'Email',
    'zh-CN': '邮箱',
  },
  phoneNumber: {
    'en-US': 'Telephone number',
    'zh-CN': '手机号码',
  },
  companyName: {
    'en-US': 'Company',
    'zh-CN': '公司',
  },
  position: {
    'en-US': 'Job title',
    'zh-CN': '职位',
  },
  countryCode: {
    'en-US': 'Region of your business',
    'zh-CN': '您所在的国家',
  },
  industry: {
    'en-US': 'Industry of your business',
    'zh-CN': '您的业务所在的行业',
  },
  consultingServices: {
    'en-US': 'What services do you require?',
    'zh-CN': '你想咨询的业务',
  },
  advertisingCountries: {
    'en-US': 'Region of advertising serving',
    'zh-CN': '期待发行地区',
  },
  howToKnowUs: {
    'en-US': 'Where did you hear about Youdao Ads?',
    'zh-CN': '如何知道YoudaoAds',
  },
};

const tooLongHelp = {
  'en-US': 'The content is too long.',
  'zh-CN': '输入内容过长',
};

const country = Object.keys(fullCountry).map((item) => {
  const zh = (fullCountry[item] as string).replace(
    /\(?\w*-?\.?\w*\)?,?\s*\w*/g,
    ''
  );
  const en = (fullCountry[item] as string)
    .replace(/[\u4e00-\u9fa5]|、/g, '')
    .trim();

  return {
    chineseName: zh,
    englishName: en,
    twoLetterCode: item,
  };
});

interface FormProps {
  handleSuccess(value: boolean): void;
}

const Form: React.FC<FormProps> = ({ handleSuccess }) => {
  const { locale } = useRouter();
  const isEn = locale === 'en-US';
  const initFormFields: FormField[] = [
    {
      name: 'name',
      required: true,
      type: 'input',
      step: 2,
      props: {
        placeholder: messages.placeholder.input[locale],
      },
    },
    {
      name: 'email',
      required: true,
      type: 'input',
      step: 2,
      props: { placeholder: messages.placeholder.input[locale] },
    },
    {
      name: 'phoneNumber',
      required: true,
      type: 'input',
      step: 2,
      props: { placeholder: messages.placeholder.input[locale] },
    },
    {
      name: 'companyName',
      required: true,
      type: 'input',
      style: { width: '100%', padding: 0 },
      step: 1,
      props: { placeholder: messages.placeholder.input[locale] },
    },
    {
      name: 'position',
      required: true,
      type: 'input',
      step: 2,
      props: { placeholder: messages.placeholder.input[locale] },
    },
    {
      name: 'countryCode',
      required: true,
      type: 'select',
      step: 1,
      props: {
        placeholder: messages.placeholder.select[locale],
        options: country.map((item) => ({
          label: isEn ? item.englishName : item.chineseName,
          value: item.twoLetterCode,
        })),
        allowSearch: true,
      },
    },
    {
      name: 'industry',
      required: true,
      type: 'select',
      step: 1,
      props: {
        options: Object.keys(industryLabels).map((item) => ({
          value: item,
          label: industryLabels[item][locale],
        })),
        placeholder: messages.placeholder.select[locale],
      },
    },
    {
      name: 'consultingServices',
      required: true,
      type: 'select',
      step: 1,
      props: {
        options: Object.keys(consultingServicesLabels).map((item) => ({
          value: item,
          label: consultingServicesLabels[item][locale],
        })),
        placeholder: messages.placeholder.select[locale],
      },
    },
    {
      name: 'advertisingCountries',
      required: true,
      type: 'select',
      step: 1,
      props: {
        placeholder: messages.placeholder.select[locale],
      },
    },
    {
      name: 'howToKnowUs',
      required: false,
      type: 'textarea',
      style: { width: '100%', padding: 0 },
      step: 2,
      props: { placeholder: messages.placeholder.input[locale] },
    },
  ];
  const [formValue, setFormValue] = useState<Partial<ContactUsParams>>({});
  const [formError, setFormError] = useState<
    Record<string, string | undefined>
  >({});
  const formValueRef = useRef<Partial<ContactUsParams>>({});
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [step, setStep] = useState<1 | 2>(1);

  const formFields = initFormFields.filter((item) => item.step === step);

  useEffect(() => {
    setFormError({});
  }, [locale]);

  const getRequiredMsg = (key: string) => {
    if (isEn) {
      return `${formLabels[key][locale]} is required`;
    }
    return `请输入${formLabels[key][locale]}`;
  };

  const getInvalidMsg = (key: string) => {
    if (isEn) {
      return `${formLabels[key][locale]} is invalid`;
    }
    return `请输入正确的${formLabels[key][locale]}`;
  };

  const getRequiredRule = (key: string) => {
    return z
      .string({ required_error: getRequiredMsg(key) })
      .min(1, getRequiredMsg(key));
  };

  const rules: Record<
    string,
    | z.ZodString
    | z.ZodNumber
    | z.ZodOptional<z.ZodString>
    | z.ZodArray<z.ZodString, 'atleastone'>
  > = {
    name: getRequiredRule('name').max(255, tooLongHelp[locale]),
    email: getRequiredRule('email')
      .regex(EMAIL_REGEXP, getInvalidMsg('email'))
      .max(255, tooLongHelp[locale]),
    phoneNumber: getRequiredRule('phoneNumber')
      .regex(PHONE_REGEXP, getInvalidMsg('phoneNumber'))
      .max(255, tooLongHelp[locale]),
    companyName: getRequiredRule('companyName').max(255, tooLongHelp[locale]),
    position: getRequiredRule('position').max(255, tooLongHelp[locale]),
    countryCode: getRequiredRule('countryCode'),
    industry: getRequiredRule('industry'),
    consultingServices: getRequiredRule('consultingServices'),
    howToKnowUs: z.string().max(1000, tooLongHelp[locale]).optional(),
    advertisingCountries: z
      .string()
      .array()
      .nonempty({
        message: getRequiredMsg('advertisingCountries'),
      }),
  };

  const handleChangeFormValue = (key: string, value: FieldValue | string[]) => {
    formValueRef.current = { ...formValueRef.current, [key]: value };
    setFormValue({ ...formValue, [key]: value });
    handleValidateField(key);
  };

  const handleCountryListChange = (value: string[]) => {
    handleChangeFormValue('advertisingCountries', value);
  };

  const handleValidateField = useCallback(
    simpleDebounce(async (key: string) => {
      const _value = formValueRef.current[key];
      const rule = rules[key];
      if (rule) {
        const result = await rule.safeParseAsync(_value);
        if (!result.success && isType<SafeParseError<FieldValue>>(result)) {
          const err = result.error.issues[0];
          return setFormError({ ...formError, [key]: err.message });
        }
        setFormError({ ...formError, [key]: undefined });
      }
    }, 200),
    [formError]
  );

  const handleSubmit = async () => {
    const _formValue = {
      ...formValue,
      industry: industryLabels[formValue.industry]?.[locale],
      consultingServices:
        consultingServicesLabels[formValue.consultingServices]?.[locale],
    };
    if (step === 1) {
      addGtagEvent(gaEventName.CONTACT_US_NEXT_CLICK);

      const rules1 = {};
      Object.keys(rules).forEach((item) => {
        if (initFormFields.find((field) => field.name === item)?.step === 1) {
          rules1[item] = rules[item];
        }
      });
      const formRule = z.object(rules1);
      const result = formRule.safeParse(_formValue);
      if (!result.success && isType<SafeParseError<FieldValue>>(result)) {
        let errs = {};
        result.error.issues.forEach((item) => {
          if (item.path.join('') === 'advertisingCountries') {
            errs = {
              ...errs,
              [item.path.join('')]: getRequiredMsg('advertisingCountries'),
            };
            return;
          }
          errs = { ...errs, [item.path.join('')]: item.message };
        });
        return setFormError(errs);
      }
      setStep(2);
      return;
    }
    const formRule = z.object(rules);
    try {
      addGtagEvent(gaEventName.CONTACT_US_SUBMIT);

      const result = formRule.safeParse(_formValue);
      if (!result.success && isType<SafeParseError<FieldValue>>(result)) {
        let errs = {};
        result.error.issues.forEach((item) => {
          errs = { ...errs, [item.path.join('')]: item.message };
        });
        return setFormError(errs);
      }
      setSubmitLoading(true);
      await contactUs(_formValue as ContactUsParams);
      setFormValue({});
      window.scrollTo({ top: 0, behavior: 'smooth' });
      handleSuccess(true);
    } catch (e) {
      Sentry.withScope((scope) => {
        scope.setLevel('warning');
        Sentry.captureException(e);
      });
      console.error(e);
      setSubmitLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <header className={styles.header} data-title="contact us">
        <h1 className={cls(styles.title, 'font')}>
          <FormattedMessage id="contact.title" />
        </h1>

        <p className={styles.description}>
          <FormattedMessage id="contact.description" />
        </p>
      </header>
      <div className={styles.wrapper}>
        {step === 2 && (
          <div className={styles.back} onClick={() => setStep(1)}>
            <FormattedMessage id="contact.back" />
          </div>
        )}
        <form
          onSubmit={() => {
            return false;
          }}
          className={styles.form}
        >
          {formFields.map(
            ({ name, type, style, required = false, props: otherProps }) => {
              const isError = !isUndefined(formError[name]);
              return (
                <div
                  key={name}
                  className={step === 1 ? styles.item : styles.item2}
                  style={style}
                >
                  <label
                    className={styles.item__label}
                    htmlFor={name}
                    aria-required={required}
                  >
                    {formLabels[name]?.[locale]}
                  </label>
                  {name === 'advertisingCountries' ? (
                    <CountryInput
                      value={formValue.advertisingCountries || []}
                      onChange={handleCountryListChange}
                      isEn={isEn}
                      isError={isError}
                    />
                  ) : (
                    <Fields
                      name={name}
                      type={type}
                      value={formValue[name]}
                      isError={isError}
                      onChange={handleChangeFormValue.bind(null, name)}
                      {...otherProps}
                    />
                  )}
                  {isError && (
                    <span className={styles.item__help}>{formError[name]}</span>
                  )}
                </div>
              );
            }
          )}
        </form>
        <div className={styles.submit__container}>
          <button
            disabled={submitLoading}
            className={styles.submit}
            onClick={handleSubmit}
          >
            {submitLoading ? (
              <span className={styles.submit__loading}>
                <Icon type="loadingOutlined" />
              </span>
            ) : (
              <FormattedMessage
                id={step === 1 ? 'contact.next' : 'contact.submit'}
              />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Form;
